import axios from 'axios';
import {
  createDecipheriv,
  createCipheriv,
  randomBytes,
} from 'crypto-browserify';

export const callApi = async (url, method, token, body = null) => {
  const headers = {
    'Content-Type': 'application/json',
  };

  // Solo agregar Authorization si hay token
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Si es superadmin y tiene una empresa seleccionada, agregar companyId a la URL
  let finalUrl = url;
  if (token) {
    try {
      const companyId = getApiCompanyId();
      if (companyId !== null && isSuperAdmin()) {
        const urlObj = new URL(url, window.location.origin);
        urlObj.searchParams.set('companyId', companyId.toString());
        finalUrl = urlObj.toString();
      }
    } catch (error) {
      // Si hay error al parsear la URL, usar la URL original
      console.warn('Error al agregar companyId a la URL:', error);
    }
  }

  const options = {
    method,
    headers,
  };

  // Solo agregar body si existe
  if (body) {
    options.body = JSON.stringify(body);
  }

  try {
  const response = await fetch(finalUrl, options);
    
    // Si la respuesta no es OK, intentar refrescar el token si es 401
  if (!response.ok) {
      if (response.status === 401 && token) {
        // Intentar refrescar el token
        const refreshed = await refreshAccessToken();
        if (refreshed) {
          // Reintentar la petición con el nuevo token
          headers.Authorization = `Bearer ${refreshed}`;
          const retryResponse = await fetch(finalUrl, { ...options, headers });
          if (retryResponse.ok) {
            // Verificar si hay contenido antes de parsear
            const contentType = retryResponse.headers.get('content-type');
            if (contentType && contentType.includes('application/json')) {
              const text = await retryResponse.text();
              return text ? JSON.parse(text) : null;
            }
            return null;
          }
        }
      }
      
      // Intentar obtener el mensaje de error, pero manejar respuestas sin cuerpo
      let errorData = null;
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        try {
          const text = await response.text();
          errorData = text ? JSON.parse(text) : null;
        } catch (parseError) {
          console.warn('Error al parsear respuesta de error:', parseError);
        }
      }
      
      // Crear un error con más información
      const errorMessage = errorData 
        ? (errorData.message || errorData.error || JSON.stringify(errorData))
        : `Error ${response.status}: ${response.statusText}`;
      const error = new Error(errorMessage);
      error.status = response.status;
      error.data = errorData;
      throw error;
    }

    // Verificar si la respuesta tiene contenido antes de parsear JSON
    const contentType = response.headers.get('content-type');
    // Si es 204 No Content o no hay content-type JSON, retornar null
    if (response.status === 204 || !contentType || !contentType.includes('application/json')) {
      return null;
    }
    
    const text = await response.text();
    return text ? JSON.parse(text) : null;
  } catch (error) {
    console.error('Error en callApi:', error);
    throw error;
  }
};

/**
 * Refresca el access token usando el refresh token guardado
 */
async function refreshAccessToken() {
  try {
    // Intentar desencriptar primero, luego JSON (compatibilidad hacia atrás)
    let tokenData = null;
    const ceaData = localStorage.getItem('cea');
    if (!ceaData) {
      return null;
    }

    try {
      // Primero intentar desencriptar
      const localToken = getTokenView(localStorage.getItem('session'));
      if (localToken) {
        tokenData = descryptedaes256cbc(ceaData, localToken);
      }
    } catch (error) {
      // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
      try {
        tokenData = JSON.parse(ceaData);
      } catch (jsonError) {
        console.error('Error leyendo token:', jsonError);
        return null;
      }
    }

    if (!tokenData || !tokenData.refresh_token) {
      return null;
    }

    const response = await fetch(`${process.env.REACT_APP_API || ''}/api/v3/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: tokenData.refresh_token,
      }),
    });

    if (response.ok) {
      const newTokenData = await response.json();
      
      // Actualizar el token en localStorage (encriptado)
      const updatedTokenData = {
        ...tokenData,
        access_token: newTokenData.access_token,
        refresh_token: newTokenData.refresh_token || tokenData.refresh_token,
      };
      const localToken = getTokenView(localStorage.getItem('session'));
      localStorage.setItem('cea', encryptedaes256cbc(updatedTokenData, localToken));
      
      return newTokenData.access_token;
    }
  } catch (error) {
    console.error('Error al refrescar token:', error);
    // Si falla el refresh, limpiar sesión
    localStorage.removeItem('cea');
    localStorage.removeItem('aec');
    window.location.href = '/#/auth/login';
  }
  return null;
}


/**
 * The function `callSendFornData` is an asynchronous function that sends a FormData object to a
 * specified URL with an optional token for authorization.
 * @param url - The `url` parameter is the endpoint URL where you want to send the form data.
 * @param token - The `token` parameter is a string that represents an authentication token. It is used
 * to authorize the request to the server. If a token is provided, it will be included in the request
 * headers as a bearer token. If no token is provided, the Authorization header will be empty.
 * @param formData - The `formData` parameter is an instance of the `FormData` object. It is used to
 * send data in a format that is compatible with HTML forms. It can be used to send files and key-value
 * pairs of data.
 * @returns the data from the response of the POST request made using axios.
 */
export const callSendFornData = async (url, token, formData) => {
  const config = {
    headers: {
      Authorization: token ? 'Bearer ' + token : '',
      // No forzar Content-Type: axios debe definir el boundary de multipart
    },
  };

  try {
    const response = await axios.post(url, formData, config);
    return response.data;
  } catch (error) {
    console.error('Error al subir la foto:', error);
    const apiMessage =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      error?.message;
    throw new Error(
      Array.isArray(apiMessage) ? apiMessage.join(', ') : apiMessage || 'Error al subir el archivo',
    );
  }
};

/**
 * The function `callApiInit` is an asynchronous function that makes an API call using the specified
 * URL, method, token, and body, and returns the response data as JSON.
 * @param url - The `url` parameter is the endpoint URL where the API request will be sent.
 * @param method - The `method` parameter is the HTTP method to be used for the API call, such as
 * 'GET', 'POST', 'PUT', 'DELETE', etc.
 * @param token - The `token` parameter is a string that represents an authentication token. It is used
 * to authorize the API request. If a token is provided, it will be included in the request headers as
 * a bearer token. If no token is provided, the Authorization header will be empty.
 * @param body - The `body` parameter is the data that you want to send in the request body. It should
 * be an object that will be converted to JSON using `JSON.stringify()` before sending the request.
 * @returns the JSON response from the API call.
 */
const INIT_TIMEOUT_MS = 15000;
const INIT_MAX_ATTEMPTS = 3;

export const callApiInit = async (url, method, token, body) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Sin body no se envía Content-Type: así el GET es una petición simple y el
  // navegador se ahorra el preflight OPTIONS antes de poder arrancar la app.
  const hasBody = body !== null && body !== undefined;
  if (hasBody) {
    headers['Content-Type'] = 'application/json';
  }

  let lastError;
  for (let attempt = 1; attempt <= INIT_MAX_ATTEMPTS; attempt++) {
    try {
      const responsecall = await axios({
        method,
        url,
        headers,
        timeout: INIT_TIMEOUT_MS,
        ...(hasBody ? { data: JSON.stringify(body) } : {}),
      });

      return responsecall.data;
    } catch (error) {
      lastError = error;

      // Si el servidor respondió, el reintento no cambia nada.
      if (error.response) {
        throw error;
      }

      if (attempt < INIT_MAX_ATTEMPTS) {
        await new Promise((resolve) => setTimeout(resolve, 400 * 2 ** (attempt - 1)));
      }
    }
  }

  throw lastError;
};

/**
 * The function `callApiLogin` is used to make an API call for logging in, with optional encryption and
 * decryption of the request and response data.
 * @param url - The `url` parameter is the URL of the API endpoint that you want to call. It specifies
 * the location where the API request should be sent.
 * @param method - The `method` parameter is the HTTP method to be used for the API call, such as
 * "GET", "POST", "PUT", "DELETE", etc.
 * @param token - The `token` parameter is a string representing an authentication token. It is used to
 * authorize the API request. If a token is provided, it will be included in the request headers as a
 * Bearer token. If no token is provided, the Authorization header will be empty.
 * @param body - The `body` parameter is an object that contains the following properties:
 * @returns the response data from the API call. If the `REACT_APP_AAA` environment variable is set to
 * `'true'`, the response data is decrypted using the `descryptedaes256cbc` function before being
 * returned.
 */
export const callApiLogin = async (url, method, token, body) => {
  // Enviar datos directamente sin encriptar
  const responsecall = await axios({
    method: method,
    url: url,
    headers: {
      Authorization: token ? 'Bearer ' + token : '',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(body),
  });

  const json = responsecall.data;
  
  // La respuesta ya viene sin encriptar
  return json;
};

/**
 * The function `callApiRegister` is an asynchronous function that makes an API call to register a
 * user, encrypting certain fields if a specific environment variable is set to true.
 * @param url - The `url` parameter is the endpoint URL where the API request will be sent.
 * @param method - The `method` parameter is the HTTP method to be used for the API request, such as
 * "GET", "POST", "PUT", "DELETE", etc.
 * @param token - The `token` parameter is a string that represents an authentication token. It is used
 * to authorize the API request.
 * @param body - The `body` parameter is an object that contains the data to be sent in the request
 * body. It typically includes properties such as `username`, `password`, `name`, `lastname`, and
 * `method`. These properties are used to encrypt the data using the `encryptedaes256cbcTextPlain
 * @returns a Promise that resolves to the response data from the API call. If the environment variable
 * `REACT_APP_AAA` is set to `'true'`, the response data is decrypted using the `descryptedaes256cbc`
 * function before being returned.
 */
export const callApiRegister = async (url, method, token, body) => {
  // Enviar datos directamente sin encriptar
  const responsecall = await axios({
    method: method,
    url: url,
    headers: {
      Authorization: token ? 'Bearer ' + token : '',
      'Content-Type': 'application/json',
    },
    data: JSON.stringify(body),
  });

  const json = responsecall.data;

  // La respuesta ya viene sin encriptar
  return json;
};
/**
 * The function `descryptedaes256cbc` takes an encrypted text and a private key, decrypts the text
 * using AES-256-CBC encryption, and returns the decrypted JSON object.
 * @param encryptedText - The `encryptedText` parameter is a string that represents the encrypted data.
 * It is expected to be in the format `IV:EncryptedData`, where `IV` is the initialization vector and
 * `EncryptedData` is the actual encrypted data. Both `IV` and `EncryptedData`
 * @param privateKey - The `privateKey` parameter is a string that represents the private key used for
 * decryption. It is used to create the decipher object for decrypting the encrypted data.
 * @returns the decrypted text as a JSON object.
 */
export function descryptedaes256cbc(encryptedText, privateKey) {
  const encryptedArray = encryptedText.split(':');
  const iv = Buffer.from(encryptedArray[0], 'hex');
  const encryptedData = Buffer.from(encryptedArray[1], 'hex');
  const decipher = createDecipheriv('aes-256-ctr', Buffer.from(privateKey), iv);
  let decrypted = decipher.update(encryptedData);
  decrypted = Buffer.concat([decrypted, decipher.final()]);
  const decryptedText = decrypted.toString();
  //console.log(decryptedText);
  return JSON.parse(decryptedText);

}

/**
 * The function `getTokenView` takes a token as input and returns the result of validating the token.
 * @param token - The `token` parameter is a string that represents a token.
 * @returns the result of the `validateToken` function.
 */
export function getTokenView(token) {
  return validateToken(token);
}
/**
 * The function encryptedaes256cbc takes in data and a private key, and encrypts the data using
 * AES-256-CBC encryption with a randomly generated initialization vector (IV) and the private key.
 * @param data - The `data` parameter is the data that you want to encrypt. It can be any type of data,
 * but it will be converted to a JSON string before encryption.
 * @param privateKey - The `privateKey` parameter is a secret key used for encryption. It should be a
 * string or a buffer containing the key value.
 * @returns the encrypted text in the format of `${iv.toString('hex')}:${encrypted.toString('hex')}`.
 */
export function encryptedaes256cbc(data, privateKey) {
  try {
    const iv = randomBytes(16);
    // console.log(iv.toString("hex"), data, privateKey);
    const cipher = createCipheriv('aes-256-ctr', Buffer.from(privateKey), iv);
    let encrypted = cipher.update(JSON.stringify(data));
    encrypted = Buffer.concat([encrypted, cipher.final()]);
    const encryptedText = `${iv.toString('hex')}:${encrypted.toString('hex')}`;

    return encryptedText;
  } catch (error) { }
}

/**
 * The function encrypts a plain text using AES-256-CBC encryption with a given private key.
 * @param data - The `data` parameter is the plain text that you want to encrypt using AES-256-CBC
 * encryption algorithm. It can be any string or binary data that you want to keep secure.
 * @param privateKey - The `privateKey` parameter is a secret key used for encryption. It should be a
 * string or a buffer containing the private key data.
 * @returns the encrypted text in the format of `${iv.toString('hex')}:${encrypted.toString('hex')}`.
 */
export function encryptedaes256cbcTextPlain(data, privateKey) {
  try {
    const iv = randomBytes(16);
    const cipher = createCipheriv('aes-256-ctr', Buffer.from(privateKey), iv);
    let encrypted = cipher.update(data);
    encrypted = Buffer.concat([encrypted, cipher.final()]);
    const encryptedText = `${iv.toString('hex')}:${encrypted.toString('hex')}`;
    return encryptedText;
  } catch (error) { }
}

/**
 * The function `validateToken` takes a token as input and returns a substring of the token by skipping
 * every 21st character.
 * @param token - The `token` parameter is a string that represents a token.
 * @returns the value of the variable `des`.
 */
export function validateToken(token) {
  if (token) {
    let b = 0;
    let des = '';
    try {
      let dat = token;
      while (b < dat.length - 20) {
        des = des + dat.charAt(b);
        b = b + 21;
      }
      return des;
    } catch (error) { }
  }
}

/**
 * The function checks if the user is active by decrypting a value stored in local storage and
 * returning true if the decrypted value has a "private" property, otherwise it returns false.
 * @returns a boolean value. It returns true if the condition `local.private` is truthy, and false
 * otherwise.
 */
export function IsUserActive() {
  const ceaData = localStorage.getItem('cea');
  if (ceaData) {
    try {
      // Primero intentar desencriptar
      const localToken = getTokenView(localStorage.getItem('session'));
      if (localToken) {
        try {
          let descrip = descryptedaes256cbc(ceaData, localToken);
          if (descrip && (descrip.private || descrip.access_token)) {
            return true;
          }
        } catch (decryptError) {
          // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
          try {
            let local = JSON.parse(ceaData);
            if (local && (local.private || local.access_token)) {
              return true;
            }
          } catch (jsonError) {
            return false;
          }
        }
      } else {
        // Si no hay localToken, intentar JSON directamente
        try {
          let local = JSON.parse(ceaData);
          if (local && (local.private || local.access_token)) {
            return true;
          }
        } catch (jsonError) {
          return false;
        }
      }
    } catch (error) {
      return false;
    }
  }
  return false;
}

/**
 * The function `UserLoginData` retrieves and decrypts user login data from local storage, returning
 * the display email if available.
 * @returns the value of `local.displayEmail` if `localStorage.getItem('cea')` exists and
 * `local.private` is truthy. Otherwise, it returns an empty string.
 */
export function UserLoginData() {
  const ceaData = localStorage.getItem('cea');
  if (ceaData) {
    try {
      // Primero intentar desencriptar
      const localToken = getTokenView(localStorage.getItem('session'));
      if (localToken) {
        try {
          let descrip = descryptedaes256cbc(ceaData, localToken);
          if (descrip && (descrip.private || descrip.access_token)) {
            return descrip.displayEmail || '';
          }
        } catch (decryptError) {
          // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
          try {
            let local = JSON.parse(ceaData);
            if (local && (local.private || local.access_token)) {
              return local.displayEmail || '';
            }
          } catch (jsonError) {
            return '';
          }
        }
      } else {
        // Si no hay localToken, intentar JSON directamente
        try {
          let local = JSON.parse(ceaData);
          if (local && (local.private || local.access_token)) {
            return local.displayEmail || '';
          }
        } catch (jsonError) {
          return '';
        }
      }
    } catch (error) {
      return '';
    }
  }
  return '';
}

export function UserData() {
  const ceaData = localStorage.getItem('cea');
  if (ceaData) {
    try {
      // Primero intentar desencriptar
      const localToken = getTokenView(localStorage.getItem('session'));
      if (localToken) {
        try {
          let descrip = descryptedaes256cbc(ceaData, localToken);
          if (descrip && (descrip.private || descrip.access_token)) {
            return descrip;
          }
        } catch (decryptError) {
          // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
          try {
            let local = JSON.parse(ceaData);
            if (local && (local.private || local.access_token)) {
              return local;
            }
          } catch (jsonError) {
            return '';
          }
        }
      } else {
        // Si no hay localToken, intentar JSON directamente
        try {
          let local = JSON.parse(ceaData);
          if (local && (local.private || local.access_token)) {
            return local;
          }
        } catch (jsonError) {
          return '';
        }
      }
    } catch (error) {
      return '';
    }
  }
  return '';
}

/**
 * Obtiene el access token del localStorage
 */
function getToken() {
  let token = '';
  try {
    const ceaData = localStorage.getItem('cea');
    if (!ceaData) {
      return token;
    }

    // Primero intentar desencriptar
    const localToken = getTokenView(localStorage.getItem('session'));
    if (localToken) {
      try {
        const decrypted = descryptedaes256cbc(ceaData, localToken);
        if (decrypted && decrypted.access_token) {
          return decrypted.access_token;
        }
      } catch (decryptError) {
        // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
        try {
          const tokenData = JSON.parse(ceaData);
          if (tokenData && tokenData.access_token) {
            return tokenData.access_token;
          }
        } catch (jsonError) {
          console.error("Error obteniendo token:", jsonError);
        }
      }
    } else {
      // Si no hay localToken, intentar JSON directamente
      try {
        const tokenData = JSON.parse(ceaData);
        if (tokenData && tokenData.access_token) {
          return tokenData.access_token;
        }
      } catch (jsonError) {
        console.error("Error obteniendo token:", jsonError);
      }
    }
  } catch (error) {
    console.error("Error obteniendo token:", error);
  }
  return token;
}

/**
 * Obtiene el companyId del usuario desde el JWT decodificado
 */
export function getCompanyId() {
  try {
    const token = getToken();
    if (!token) return null;
    
    // Decodificar el JWT (solo la parte del payload, sin verificar firma)
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const payload = JSON.parse(jsonPayload);
    return payload.companyId || null;
  } catch (error) {
    console.error('Error obteniendo companyId:', error);
    return null;
  }
}

/**
 * Obtiene el userId del usuario autenticado desde el JWT (`sub`).
 */
export function getUserId() {
  try {
    const token = getToken();
    if (!token) return null;

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    const payload = JSON.parse(jsonPayload);
    const userId = Number(payload.sub);
    return Number.isFinite(userId) ? userId : null;
  } catch (error) {
    console.error('Error obteniendo userId:', error);
    return null;
  }
}

/**
 * Obtiene is_superadmin del usuario desde el JWT decodificado
 */
export function isSuperAdmin() {
  try {
    const token = getToken();
    if (!token) return false;
    
    // Decodificar el JWT (solo la parte del payload, sin verificar firma)
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    
    const payload = JSON.parse(jsonPayload);
    return payload.is_superadmin === true;
  } catch (error) {
    console.error('Error obteniendo is_superadmin:', error);
    return false;
  }
}

/**
 * Obtiene el companyId seleccionado por el superadmin (si existe)
 */
export function getSelectedCompanyId() {
  return localStorage.getItem('selectedCompanyId') ? parseInt(localStorage.getItem('selectedCompanyId')) : null;
}

/**
 * Guarda el companyId seleccionado por el superadmin
 */
export function setSelectedCompanyId(companyId) {
  if (companyId) {
    localStorage.setItem('selectedCompanyId', companyId.toString());
  } else {
    localStorage.removeItem('selectedCompanyId');
  }
}

/**
 * Obtiene el companyId a usar en las peticiones API
 * Si es superadmin, siempre retorna null para ver todas las empresas
 * Si no, usa el companyId del usuario
 */
export function getApiCompanyId() {
  if (isSuperAdmin()) {
    // Superadmin siempre ve todas las empresas, no filtra por companyId
    return null;
  }
  return getCompanyId();
}




